import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  PageContainer, 
  FormSection as CommonFormSection, 
  FormGroup as CommonFormGroup, 
  Label as CommonLabel, 
  Input, 
  FormRow as CommonFormRow, 
  ErrorMessage as CommonErrorMessage
} from '../styles/CommonStyles';
import { generateId, saveToLocalStorage, loadFromLocalStorage, debounce, formatTimestamp } from '../utils/helpers';
import { STORAGE_KEYS, DEFAULT_IDS } from '../constants';
// VersionControl import removed per Miles 2026-04-06
import { Version } from '../types/common';
import { Link } from 'react-router-dom';
// PromptBar import removed per Miles 2026-04-06
import { useResponsive } from '../hooks/useResponsive';
import { saveProfileData, generateUniqueId as generateUniqueProfileId, migrateAllProfileIds, getOrCreateUserProfile } from '../utils/profileManager';
import { useAuth } from '../context/AuthContext';
import GlobalFooter from './GlobalFooter';
import db from '../utils/firestore';
import { doc, setDoc, getDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { loadUserContext } from '../lib/identity/context';
import { getAuth } from 'firebase/auth';
import { AnonymousAuthService } from '../utils/anonymousAuth';
import SeoHead from './SeoHead';
import { ORG_SIZE_BANDS } from '../config/planRecommendation';
import { readRepairProfile } from '../services/profileWriteThrough';
import {
  SmsConsentState,
  maskPhone,
  requestSmsVerification,
  subscribeSmsConsent,
} from '../services/smsVerificationClient';
import {
  ensureOrgFactsForWorkspace,
  loadOrganization,
  orgIdForWorkspace,
} from '../services/organizations';
import { fetchWorkspaceOptionsCached } from '../services/workspaceOptionsCache';
import { workspaceLabel } from '../services/workspaceIdentity';

// Viewport meta for proper mobile display. Never disable pinch-zoom
// (maximum-scale / user-scalable) — that is a WCAG 1.4.4 failure.
const ViewportMeta = () => (
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
);

// Add a GlobalMobileStyle component
const GlobalMobileStyle = styled.div`
  /* Mobile-first approach - start with styles for smallest screens */
  .mobile-prompt-bar-zone {
    height: 5rem; /* Space for the prompt bar */
    position: relative;
    width: 100%;
    pointer-events: none;
    margin-top: 1.5rem;
  }
  
  /* Hide version control on mobile and close-to-mobile */
  @media (max-width: 1024px) {
    .version-control-container, 
    [data-testid="version-control"],
    [class*="version-control"],
    [id*="version-control"] {
      display: none;
    }
  }
`;

// Screen reader only text
const ScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

/* Main container - mobile-first approach */
const ProfileContainer = styled(PageContainer)`
  /* Mobile first approach - base styles for all devices */
  padding: 1.5rem 1rem;
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
  /* Bottom padding for the floating prompt bar */
  padding-bottom: 7rem;
  background: var(--brand-canvas);
  
  /* Tablet (768px) */
  @media (min-width: 768px) {
    padding: 2rem 1.5rem;
    padding-bottom: 7rem;
  }
  
  /* Laptop (1024px) */
  @media (min-width: 1024px) {
    max-width: 900px;
    padding: 2.5rem;
    min-height: calc(100vh - 160px);
  }
  
  /* Desktop (1200px) */
  @media (min-width: 1200px) {
    max-width: 1000px;
  }
`;

// Add a fadeIn animation for the prompt bar
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animated fade-in wrapper for content
const FadeIn = styled.div`
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  animation: fadeIn 0.5s ease-out forwards;
`;

// MobilePromptBarWrapper removed per Miles 2026-04-06

// Enhanced form section with improved mobile responsiveness
const FormSection = styled.div`
  /* Mobile-first approach */
  background: white;
  padding: 1.5rem;
  border-radius: 1rem;
  box-shadow: 0 0.375rem 1.25rem rgba(26, 26, 26, 0.04);
  border: 1px solid var(--brand-line);
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  width: 100%;

  /* Tablet and above */
  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }

  /* Desktop */
  @media (min-width: 1024px) {
    padding: 2.75rem;

    &:hover {
      box-shadow: 0 0.75rem 2.5rem rgba(198, 167, 94, 0.08);
      transform: translateY(-3px);
    }
  }
`;

const SectionTitle = styled.h3`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--brand-black);
  font-size: 1.25rem; /* Smaller on mobile */
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  letter-spacing: -0.02em;
  text-transform: lowercase;
  position: relative;
  display: inline-block;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin: 0 0 2.75rem 0;
  }
`;

const ProfileSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  @media (min-width: 768px) {
    gap: 2.5rem;
  }
`;

const Section = styled.div`
  margin-bottom: 1rem;
`;

const SectionHeading = styled.h4`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--brand-charcoal);
  font-size: 1rem;
  font-weight: 500;
  margin: 0 0 1rem 0;
  letter-spacing: -0.01em;
  text-transform: lowercase;
  border-bottom: 1px solid var(--brand-line);
  padding-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
    margin: 0 0 1.5rem 0;
    padding-bottom: 1rem;
  }
`;

// Mobile-optimized ID section
const IdSection = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  position: relative;
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
  }
`;

const IdIcon = styled.div`
  width: 42px;
  height: 42px;
  background: rgba(198, 167, 94, 0.08);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--brand-gold);
  font-size: 1.25rem;
  box-shadow: 0 4px 12px rgba(198, 167, 94, 0.1);
  
  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const IdContent = styled.div`
  flex: 1;
`;

const IdLabel = styled(CommonLabel)`
  text-transform: lowercase;
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--brand-text-secondary);
  letter-spacing: 0.02em;
`;

const ReadOnlyField = styled.div`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 1rem; /* Smaller on mobile */
  padding: 0.75rem;
  background: var(--brand-canvas);
  border: 1px solid var(--brand-line);
  border-radius: 0.75rem;
  color: var(--brand-charcoal);
  width: 100%;
  user-select: all;
  letter-spacing: 0.15em;
  text-align: center;
  font-weight: 500;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.02);
  transition: all 0.2s ease;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
    padding: 0.875rem 1rem;
    letter-spacing: 0.2em;
    border-radius: 12px;
  }
  
  &:hover {
    background: var(--brand-surface);
    border-color: var(--brand-line-strong);
  }

  &::selection {
    background: rgba(198, 167, 94, 0.15);
  }
`;

const Label = styled(CommonLabel)`
  text-transform: lowercase;
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--brand-text-secondary);
  letter-spacing: 0.02em;
  transition: color 0.2s ease;
  
  @media (min-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
  width: 100%;
  
  @media (min-width: 768px) {
    flex-direction: row;
    gap: 2rem;
    margin-bottom: 1.75rem;
  }
`;

const FormGroup = styled.div`
  flex: 1;
  position: relative;
  
  &:hover ${Label} {
    color: var(--brand-gold);
  }
`;

const inputStyles = `
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid var(--brand-line);
  transition: all 0.25s ease;
  border-radius: 0.75rem;
  background: var(--brand-canvas);
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.01);
  min-height: 44px; /* Ensure touch target size */

  @media (min-width: 768px) {
    padding: 1rem 1.25rem;
    border-radius: 12px;
  }

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.1);
    background: white;
  }

  &::placeholder {
    color: var(--brand-text-muted);
    font-weight: 300;
  }

  &:hover {
    border-color: var(--brand-line-strong);
  }
`;

const StyledInput = styled(Input)<{ isValid?: boolean }>`
  ${inputStyles}
  ${props => props.isValid && `
    border-color: var(--brand-gold);
    background-color: rgba(198, 167, 94, 0.05);
  `}

  &::placeholder {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 0.9rem;
    letter-spacing: normal;
    color: var(--color-text-placeholder);
  }
`;

const NameInput = styled(StyledInput)`
  font-weight: 500;

  &::placeholder {
    font-weight: normal;
  }
`;


const PhoneInput = styled(StyledInput)`
  letter-spacing: 0.02em;
  font-family: 'SF Mono', 'Consolas', monospace;
  
  &::placeholder {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    letter-spacing: normal;
  }
`;

const EmailInput = styled(StyledInput)`
  letter-spacing: 0.01em;
  font-size: 0.95rem;
  padding-right: 1.5rem;
  
  &::placeholder {
    letter-spacing: normal;
    font-size: 0.9rem;
  }
`;

// Responsive short input for postal codes and state
const ShortInput = styled(StyledInput)`
  font-family: 'SF Mono', 'Consolas', monospace;
  
  &::placeholder {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  }
`;

const TextArea = styled.textarea`
  ${inputStyles}
  min-height: 80px;
  line-height: 1.5;
  resize: vertical;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  
  &::placeholder {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 0.9rem;
    letter-spacing: normal;
    color: var(--color-text-placeholder);
  }
`;

const UrlInput = styled(StyledInput)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.9rem;
  letter-spacing: 0;
  
  @media (min-width: 768px) {
    font-size: 0.95rem;
    padding: 1rem 1.25rem;
  }
  
  &::placeholder {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    letter-spacing: normal;
    font-size: 0.9rem;
  }
`;

const ErrorMessage = styled(CommonErrorMessage)`
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: rgba(244, 67, 54, 0.08);
  border-radius: 8px;
  font-size: 0.8rem;
  border-left: 3px solid #f44336;
`;

// Enhanced save indicator
const SaveIndicator = styled.div<{ visible: boolean, isError?: boolean }>`
  position: fixed;
  bottom: 5rem; /* Position above the prompt bar on mobile */
  right: 1rem;
  background: ${props => props.isError ? '#ff5252' : 'var(--brand-gold)'};
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 30px;
  font-size: 0.875rem;
  opacity: ${props => props.visible ? 1 : 0};
  transform: translateY(${props => props.visible ? 0 : '10px'});
  transition: all 0.3s ease;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
  
  @media (min-width: 1025px) {
    bottom: 1.5rem;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Export Section Styles with mobile optimization
const ExportSection = styled(FormSection)`
  position: relative;
  overflow: hidden;
`;

const ExportTitle = styled(SectionTitle)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ComingSoonBadge = styled.span`
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  color: white;
  padding: 0.35rem 0.75rem;
  border-radius: 30px;
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(198, 167, 94, 0.2);
  display: inline-block;
`;

const ExportDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.7;
  color: var(--brand-text-secondary);
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
    max-width: 800px;
  }
`;

// Mobile-optimized feature list
const ExportFeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    gap: 1.5rem;
    margin-top: 2rem;
  }
`;

// Enhanced export feature with better touch interactions
const ExportFeature = styled.div`
  background: rgba(198, 167, 94, 0.04);
  border-radius: 0.75rem;
  padding: 1.25rem;
  flex: 1;
  border: 1px solid rgba(198, 167, 94, 0.1);
  transition: all 0.2s ease;

  @media (min-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;

    &:hover {
      background: rgba(198, 167, 94, 0.07);
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(198, 167, 94, 0.1);
    }
  }

  /* Touch highlight for mobile */
  &:active {
    background: rgba(198, 167, 94, 0.07);
    transform: scale(0.98);
  }
`;

const FeatureTitle = styled.h4`
  font-size: 1rem;
  color: var(--brand-black);
  margin-bottom: 0.5rem;
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
`;

// Add the missing FeatureDescription component
const FeatureDescription = styled.p`
  font-size: 0.85rem;
  color: var(--brand-text-secondary);
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

// Collapsible section for mobile view
const CollapsibleSection = styled.div<{ isCollapsed: boolean }>`
  overflow: hidden;
  max-height: ${props => props.isCollapsed ? '60px' : '2000px'};
  transition: max-height 0.5s ease-in-out;
  position: relative;
  
  &::after {
    content: '';
    display: ${props => props.isCollapsed ? 'block' : 'none'};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1));
    pointer-events: none;
  }
  
  @media (min-width: 768px) {
    max-height: none;
    overflow: visible;
    
    &::after {
      display: none;
    }
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: var(--brand-gold);
  font-size: 0.875rem;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
  min-height: 44px;
  transition: all 0.2s ease;

  &:hover {
    color: var(--brand-gold-hover);
    text-decoration: underline;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    transition: transform 0.3s ease;
  }
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const Toggle = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  margin-right: 1rem;
  cursor: pointer;
  background: ${props => props.active ? 'rgba(198, 167, 94, 0.1)' : 'transparent'};
  color: ${props => props.active ? 'var(--brand-gold)' : 'var(--brand-text-secondary)'};
  transition: all 0.3s ease;

  &:hover {
    background: rgba(198, 167, 94, 0.05);
  }
`;

const GoogleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  background: transparent;
  border-radius: 50%;
  cursor: help; /* Shows a question mark cursor to indicate it has a tooltip */
`;

const GoogleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 10px;
  color: white;
  background: #4285F4;
  border-radius: 50%;
  font-weight: bold;
`;

// Google Account Indicator
const GoogleAccountIndicator = styled.div`
  background: rgba(198, 167, 94, 0.04);
  border-radius: 0.75rem;
  padding: 0.75rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: var(--brand-black);
  font-weight: 500;
`;

const EmailBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  background: transparent;
  border-radius: 50%;
  cursor: help; /* Shows a question mark cursor to indicate it has a tooltip */
  
  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    font-size: 10px;
    color: white;
    background: var(--brand-gold);
    border-radius: 50%;
    font-weight: bold;
  }
`;

const FooterWrapper = styled.div`
  width: 100%;
  margin-top: auto;
`;

// Phone-verification status chip (2026-08-14): sits next to the primaryPhone
// input, driven live by the SMS consent doc.
const PhoneVerifyChip = styled.span<{ $tone: 'green' | 'gold' | 'gray' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.4rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  ${p => p.$tone === 'green'
    ? 'background: #E8F5E9; border: 1px solid #C8E6C9; color: #2e7d32;'
    : p.$tone === 'gold'
      ? 'background: #FBF8F0; border: 1px solid #E8DFC4; color: #8B7333;'
      : 'background: #F5F5F5; border: 1px solid var(--brand-line); color: #666;'}
`;

interface ProfileData {
  memberId: string;
  accountId: string;
  firstName: string;
  lastName: string;
  primaryPhone: string;
  secondaryPhone: string;
  email: string;
  secondaryEmail: string;
  streetAddress: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  xUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  telegramUrl?: string;
  facebookUrl?: string;
}

// Interface for user accounts
interface UserAccount {
  email: string;
  timestamp: string;
  source: 'chat' | 'signup';
  metadata?: Record<string, any>;
}

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  primaryPhone?: string;
  secondaryPhone?: string;
  email?: string;
  secondaryEmail?: string;
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  xUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  telegramUrl?: string;
  facebookUrl?: string;
}

const Profile: React.FC = () => {
  // Get the current authenticated user
  const { user, isAuthenticated } = useAuth();
  const firebaseAuth = getAuth();
  const anonymousAuthService = AnonymousAuthService.getInstance();
  
  // State management for profile data
  const [profileData, setProfileData] = useState<ProfileData>(() => {
    // Default profile with unique IDs (fallback for initial state)
    const defaultProfile: ProfileData = {
      memberId: generateUniqueProfileId('member'),
      accountId: generateUniqueProfileId('account'),
      firstName: '',
      lastName: '',
      primaryPhone: '',
      secondaryPhone: '',
      email: '',
      secondaryEmail: '',
      streetAddress: '',
      streetAddress2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      xUrl: '',
      linkedinUrl: '',
      youtubeUrl: '',
      telegramUrl: '',
      facebookUrl: ''
    };

    // Load from localStorage as fallback while Firebase loads
    const savedProfileData = loadFromLocalStorage<ProfileData>(STORAGE_KEYS.PROFILE_DATA, defaultProfile);
    
    // If IDs are default/anonymized values, replace them with unique IDs
    if (savedProfileData.memberId === DEFAULT_IDS.MEMBER_ID ||
        savedProfileData.accountId === DEFAULT_IDS.ACCOUNT_ID) {
      savedProfileData.memberId = generateUniqueProfileId('member');
      savedProfileData.accountId = generateUniqueProfileId('account');
      console.log('Replaced default IDs with unique IDs');
    }
    
    return savedProfileData;
  });
  
  // Add state for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState({
    contactInfo: false,
    addressInfo: false,
    exportInfo: false
  });
  
  const toggleSection = (section: keyof typeof collapsedSections) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Organization card (re-keyed 2026-08-16, multi-org D1): shows the ACTIVE
  // WORKSPACE's organization — every workspace shows ITS OWN name here, never
  // a user-global field (the "both profiles show the same company" bug).
  // Editing lives on /organization (the owner's "own page" directive); this
  // card is the read-only projection plus the door.
  const [orgCard, setOrgCard] = useState<{ name: string; sizeBand: string; role: string } | null>(null);
  const [orgCardLoaded, setOrgCardLoaded] = useState(false);
  useEffect(() => {
    const uid = firebaseAuth.currentUser?.uid;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        // Same load-speed fixes as /organization (2026-08-25): pass the uid so
        // the workspace list can be served from its persisted copy instead of
        // a full callable round trip, and run the ensure ALONGSIDE the org
        // read rather than in front of it — ensureOrgFacts only creates docs
        // for a member missing them, and the orgId it returns is derived from
        // the accountId, so the read never depended on it. Re-read only when
        // the ensure named a different org or just created a doc this read
        // found missing.
        const opts = await fetchWorkspaceOptionsCached(uid);
        const activeWs = opts.find((o) => o.isActive) || opts[0] || null;
        const accountId = activeWs?.accountId || uid;
        const derivedOrgId = orgIdForWorkspace(accountId);
        const ensurePromise = ensureOrgFactsForWorkspace(accountId);
        let orgDoc = await loadOrganization(derivedOrgId);
        const ensured = await ensurePromise;
        const orgId = ensured?.orgId || derivedOrgId;
        if (orgId !== derivedOrgId || (ensured !== null && !orgDoc)) {
          orgDoc = await loadOrganization(orgId);
        }
        if (cancelled) return;
        setOrgCard({
          name: orgDoc?.name || (activeWs ? workspaceLabel(activeWs, uid) : ''),
          sizeBand: orgDoc?.size_band || '',
          role: ensured?.role || '',
        });
        setOrgCardLoaded(true);
      } catch (e) {
        console.warn('organization card load failed:', e);
        if (!cancelled) setOrgCardLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseAuth.currentUser?.uid]);
  
  // State for validation errors and valid fields
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [validFields, setValidFields] = useState<Record<string, boolean>>({}); // Track fields with valid input
  
  // State for save indicator
  const [saveIndicator, setSaveIndicator] = useState({
    visible: false,
    isError: false,
    message: ''
  });
  
  // Use the responsive hook to detect screen size
  const { isMobile, isTablet } = useResponsive();
  
  // Add state to detect mobile view
  const [isMobileView, setIsMobileView] = useState(false);
  
  // Version control state management
  const [versions, setVersions] = useState<Version[]>(() => {
    const savedVersions = loadFromLocalStorage<Version[]>(STORAGE_KEYS.VERSIONS, []);
    
    if (savedVersions.length === 0) {
      // Initialize with a default version if none exists
      return [{
        id: 'current',
        label: formatTimestamp(new Date()),
        timestamp: new Date().toISOString(),
        changes: [{ type: 'profile', action: 'add', target: 'initial', details: 'Initial profile setup' }],
        snapshot: { profileData: profileData }
      }];
    }
    
    return savedVersions;
  });
  
  const [selectedVersion, setSelectedVersion] = useState<string>(() => 
    loadFromLocalStorage(STORAGE_KEYS.SELECTED_VERSION, 'current')
  );
  
  const [pendingChanges, setPendingChanges] = useState<Version['changes']>([]);

  // Phase 3 (Novel-Form): "Your Prest0n Number" — read-only card surfacing the
  // "Your Prest0n Number" card removed (owner directive 2026-08-16): it never
  // populated for real users and read as noise. The accounts/{uid}/phone/main
  // provisioning data still exists server-side if a future surface wants it.

  // Phone verification (2026-08-14): live SMS double-opt-in status for the
  // status chip next to the primaryPhone input, plus a ref mirror so the
  // once-created save function below can consult it without going stale.
  const [smsConsent, setSmsConsent] = useState<SmsConsentState>({ status: 'none', phoneMasked: null });
  const smsConsentRef = useRef<SmsConsentState>(smsConsent);
  useEffect(() => { smsConsentRef.current = smsConsent; }, [smsConsent]);
  useEffect(() => {
    const uid = firebaseAuth.currentUser?.uid;
    if (!uid) {
      setSmsConsent({ status: 'none', phoneMasked: null });
      return;
    }
    return subscribeSmsConsent(uid, setSmsConsent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseAuth.currentUser?.uid, isAuthenticated]);

  // When auth state changes, ensure we load the profile associated with that email (IDs per account)
  useEffect(() => {
    if (!isAuthenticated || !user?.email) return;
    try {
      // Pull or create the correct profile for this email using central manager (ensures unique IDs per email)
      const resolved = getOrCreateUserProfile(user.email, user.googleAuth ? { email: user.email, firstName: user.firstName, lastName: user.lastName } : undefined);
      // Keep any locally edited fields that are non-email if they exist and differ
      setProfileData(prev => {
        if (!prev) return resolved as any;
        return {
          ...resolved,
          // Preserve existing form values the user may be editing (except IDs/email)
          firstName: prev.firstName || resolved.firstName,
          lastName: prev.lastName || resolved.lastName,
          primaryPhone: prev.primaryPhone || resolved.primaryPhone,
          secondaryPhone: prev.secondaryPhone || resolved.secondaryPhone,
          streetAddress: prev.streetAddress || resolved.streetAddress,
          streetAddress2: prev.streetAddress2 || resolved.streetAddress2,
          city: prev.city || resolved.city,
          state: prev.state || resolved.state,
          postalCode: prev.postalCode || resolved.postalCode,
          country: prev.country || resolved.country,
          xUrl: prev.xUrl || resolved.xUrl,
          linkedinUrl: prev.linkedinUrl || resolved.linkedinUrl,
          youtubeUrl: prev.youtubeUrl || resolved.youtubeUrl,
          telegramUrl: prev.telegramUrl || resolved.telegramUrl,
          facebookUrl: prev.facebookUrl || resolved.facebookUrl
        } as any;
      });
      // Persist cache for the active user
      saveToLocalStorage(STORAGE_KEYS.PROFILE_DATA, resolved as any);
      saveToLocalStorage(STORAGE_KEYS.USER_PROFILE, resolved as any);
    } catch (e) {
      console.error('Failed to resolve profile for current user:', e);
    }
  }, [isAuthenticated, user?.email]);

  // Clear in-memory profile fields on logout (without touching Firestore or localStorage)
  useEffect(() => {
    if (!isAuthenticated) {
      setProfileData({
        accountId: '',
        memberId: '',
        firstName: '',
        lastName: '',
        primaryPhone: '',
        secondaryPhone: '',
        email: '',
        secondaryEmail: '',
        streetAddress: '',
        streetAddress2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        xUrl: '',
        linkedinUrl: '',
        youtubeUrl: '',
        telegramUrl: '',
        facebookUrl: ''
      });
    }
  }, [isAuthenticated]);

  // Firebase sync when profile data changes
  useEffect(() => {
    if (!isAuthenticated) return; // Do not save anything while logged out
    if (Object.keys(profileData).length > 0) {
      // Always ensure we're saving with the authenticated user's email if available
      let finalProfileData = profileData;
      if (isAuthenticated && user?.email && profileData.email !== user.email) {
        finalProfileData = {
          ...profileData,
          email: user.email
        };
      }
      
      // Save to both Firebase and localStorage (localStorage as cache)
      debouncedSaveToFirebase(finalProfileData);
      debouncedSave(finalProfileData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData, isAuthenticated, user]);
  
  // Migrate all profiles to ensure they have 9-digit account IDs
  useEffect(() => {
    // Run once on component mount
    migrateAllProfileIds();
  }, []);
  
  // Mapper: Firestore <-> UI
  function mapFirestoreToUi(docData: any): ProfileData | null {
    if (!docData || !Array.isArray(docData.profiles) || docData.profiles.length === 0) return null;
    const p = docData.profiles[0] || {};
    return {
      accountId: p.account_id || generateUniqueProfileId('account'),
      memberId: p.member_id || generateUniqueProfileId('member'),
      firstName: p.first_name || '',
      lastName: p.last_name || '',
      primaryPhone: p.phone || '',
      secondaryPhone: p.secondary_phone || '',
      email: p.email || '',
      secondaryEmail: p.secondary_email || '',
      streetAddress: p.street_address || '',
      streetAddress2: p.street_address_line_2 || '',
      city: p.city || '',
      state: p.state_province || p.state || '',
      postalCode: p.postal_code || '',
      country: p.country || '',
      xUrl: p.x || '',
      linkedinUrl: p.linkedin || '',
      youtubeUrl: p.youtube || '',
      telegramUrl: p.telegram || '',
      facebookUrl: p.facebook || ''
    };
  }

  function mapUiToFirestore(p: ProfileData): any {
    return {
      // Do not generate new IDs here; immutability enforced later during save
      account_id: p.accountId || '',
      member_id: p.memberId || '',
      first_name: p.firstName || '',
      last_name: p.lastName || '',
      phone: p.primaryPhone || '',
      secondary_phone: p.secondaryPhone || '',
      email: p.email || '',
      secondary_email: p.secondaryEmail || '',
      street_address: p.streetAddress || '',
      street_address_line_2: p.streetAddress2 || '',
      city: p.city || '',
      state_province: p.state || '',
      postal_code: p.postalCode || '',
      country: p.country || '',
      x: p.xUrl || '',
      linkedin: p.linkedinUrl || '',
      youtube: p.youtubeUrl || '',
      telegram: p.telegramUrl || '',
      facebook: p.facebookUrl || ''
    };
  }

  // Load profile data from Firebase when user changes (login/logout)
  useEffect(() => {
    const loadProfileFromFirebase = async () => {
      try {
        // Ensure Firebase Auth is ready
        let firebaseUser = firebaseAuth.currentUser;
        
        if (!firebaseUser && user?.id) {
          console.log('🔐 PROFILE: Real session required; redirecting to /auth');
          try { await anonymousAuthService.ensureAuthenticated(); } catch { return; }
          firebaseUser = firebaseAuth.currentUser;
        }
        
        if (!firebaseUser?.uid) {
          console.log('⚠️ PROFILE: No Firebase user available, using localStorage fallback');
          return;
        }
        
        console.log('📥 PROFILE: Loading UID-centric user context for user:', firebaseUser.uid);

        try { await loadUserContext(firebaseUser.uid); } catch {}

        // Read-repair (Phase 0, 2026-08-15 owner report): accounts that
        // finished onboarding before the write-through existed have their
        // answers stranded in accounts/{uid}/onboarding/profile — back-fill
        // the empty fields BEFORE reading, merge-if-empty, so the page shows
        // what the user already told us. Existing edits always win.
        try {
          await readRepairProfile({ firstName: user?.firstName, lastName: user?.lastName });
        } catch { /* non-fatal — the page renders whatever exists */ }

        // Primary: new schema users/{uid}/user_profile/user_profile
        const profileDocRef = doc(db, 'users', firebaseUser.uid, 'user_profile', 'user_profile');
        const profileDoc = await getDoc(profileDocRef);
        if (profileDoc.exists()) {
          const data = profileDoc.data() as any;
          const mapped = mapFirestoreToUi(data);
          if (mapped) {
            setProfileData(mapped);
            saveToLocalStorage(STORAGE_KEYS.PROFILE_DATA, mapped);
            return;
          }
        }

        // If no profile doc, keep local state; user doc creation handled by loadUserContext
        
      } catch (error) {
        console.error('❌ PROFILE: Error loading profile from Firebase:', error);
      }
    };

    if (isAuthenticated && user) {
      loadProfileFromFirebase();
    }
  }, [isAuthenticated, user]);
  
  // Set up real-time Firebase listener for profile updates
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const setupFirebaseListener = async () => {
      try {
        // Ensure Firebase Auth is ready
        let firebaseUser = firebaseAuth.currentUser;
        
        if (!firebaseUser) {
          try { await anonymousAuthService.ensureAuthenticated(); } catch { return; }
          firebaseUser = firebaseAuth.currentUser;
        }
        
        if (!firebaseUser?.uid) return;
        
        console.log('🔄 PROFILE: Setting up real-time listener for user:', firebaseUser.uid);
        
        const userDocRef = doc(db, 'users', firebaseUser.uid, 'user_profile', 'user_profile');
        
        // Set up real-time listener on new schema
        const unsubscribe = onSnapshot(userDocRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data() as any;
            const mapped = mapFirestoreToUi(data);
            if (mapped) {
              console.log('🔄 PROFILE: Received real-time update from Firebase');
              setProfileData(mapped);
              saveToLocalStorage(STORAGE_KEYS.PROFILE_DATA, mapped);
            }
          }
        });
        
        // Return cleanup function
        return unsubscribe;
      } catch (error) {
        console.error('❌ PROFILE: Error setting up Firebase listener:', error);
      }
    };

    setupFirebaseListener();
  }, [isAuthenticated, user]);
  
  // Check for updated Google auth email on mount and in localStorage changes (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;
    // Function to check and update email from various sources
    const checkAndUpdateEmail = () => {
      if (!profileData.email || profileData.email.trim() === '') {
        // Try getting from user_accounts
        const userAccountsStr = localStorage.getItem('user_accounts');
        const userAccounts = userAccountsStr ? JSON.parse(userAccountsStr) : [];
        
        if (userAccounts && userAccounts.length > 0) {
          // Find the most recent account email
          const latestAccount = userAccounts.reduce((latest: UserAccount | null, account: UserAccount) => {
            if (!latest || new Date(account.timestamp) > new Date(latest.timestamp)) {
              return account;
            }
            return latest;
          }, null);
          
          if (latestAccount && latestAccount.email) {
            setProfileData(prevData => ({
              ...prevData,
              email: latestAccount.email
            }));
            console.log('Updated profile with email from user account:', latestAccount.email);
          }
        }
        
        // Also check auth token
        const authToken = localStorage.getItem('auth_token');
        if (authToken) {
          try {
            const tokenData = JSON.parse(atob(authToken.split('.')[1]));
            if (tokenData && tokenData.email) {
              setProfileData(prevData => ({
                ...prevData,
                email: tokenData.email
              }));
              console.log('Updated profile with email from auth token:', tokenData.email);
            }
          } catch (e) {
            console.warn('Could not parse auth token for email');
          }
        }
      }
    };
    
    // Check on component mount
    checkAndUpdateEmail();
    
    // Also listen for storage events (in case user logs in via Google in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' || e.key === 'user_accounts') {
        checkAndUpdateEmail();
      }
      
      // Handle auth state changed event for Google users
      if (e.key === 'auth_state_changed' && e.newValue) {
        try {
          const authState = JSON.parse(e.newValue);
          if (authState.googleAuth && authState.email && authState.email.trim() !== '') {
            console.log('Detected Google auth state change for:', authState.email);
            
            // Create a minimal Google data profile to update with
            const googleData = {
              email: authState.email
            };
            
            // Force update profile with Google data using just the email
            const updatedProfile = getOrCreateUserProfile(authState.email, googleData);
            setProfileData(updatedProfile);
            console.log('Profile updated with Google data from auth state change');
          }
        } catch (error) {
          console.error('Error handling auth state change:', error);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [profileData.email, isAuthenticated]);
  
  // Handle profile data initialization with 9-digit IDs validation
  useEffect(() => {
    let updatedProfile = {...profileData};
    let needsUpdate = false;
    
    // Check if the loaded profile has a valid 9-digit account ID
    if (profileData && profileData.accountId) {
      const isValidAccountId = /^[1-9]\d{8}$/.test(profileData.accountId);
      
      if (!isValidAccountId) {
        console.log(`Profile account ID needs migration: ${profileData.accountId}`);
        
        // Generate a new 9-digit account ID
        const firstDigit = Math.floor(Math.random() * 9) + 1;
        const remainingDigits = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join('');
        const newAccountId = `${firstDigit}${remainingDigits}`;
        
        updatedProfile.accountId = newAccountId;
        needsUpdate = true;
        
        console.log(`Migrated profile account ID: ${profileData.accountId} → ${newAccountId}`);
      }
    }
    
    // Check if the loaded profile has a valid 9-digit member ID
    if (profileData && profileData.memberId) {
      const isValidMemberId = /^[1-9]\d{8}$/.test(profileData.memberId);
      
      if (!isValidMemberId) {
        console.log(`Profile member ID needs migration: ${profileData.memberId}`);
        
        // Generate a new 9-digit member ID
        const firstDigit = Math.floor(Math.random() * 9) + 1;
        const remainingDigits = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join('');
        const newMemberId = `${firstDigit}${remainingDigits}`;
        
        updatedProfile.memberId = newMemberId;
        needsUpdate = true;
        
        console.log(`Migrated profile member ID: ${profileData.memberId} → ${newMemberId}`);
      }
    }
    
    // Update profile data if needed
    if (needsUpdate) {
      setProfileData(updatedProfile);
    }
  }, []); // Only run on initial mount
  
  // Function to save profile data to Firebase users/{uid}/user_profile/user_profile
  const isAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => { isAuthenticatedRef.current = isAuthenticated; }, [isAuthenticated]);
  
  const saveProfileToFirebase = useRef(async (data: ProfileData) => {
    // Do not perform any cloud writes if logged out
    if (!isAuthenticatedRef.current) {
      return;
    }
    try {
      // Ensure Firebase Auth is ready
      let firebaseUser = firebaseAuth.currentUser;
      
      if (!firebaseUser) {
        console.log('🔐 PROFILE: Real session required; redirecting to /auth');
        try { await anonymousAuthService.ensureAuthenticated(); } catch { throw new Error('No authenticated user'); }
        firebaseUser = firebaseAuth.currentUser;
      }
      
      if (!firebaseUser?.uid) {
        console.error('❌ PROFILE: No Firebase user available for saving profile');
        throw new Error('No authenticated user');
      }
      
      console.log('✅ PROFILE: Saving profile data under users/{uid}/user_profile/user_profile for user:', firebaseUser.uid);

      // Guard cross-UID writes
      try { (await import('../lib/identity/writes')).addUserScopedWriteGuard(firebaseUser.uid, 'Profile.save'); } catch {}

      const ref = doc(db, 'users', firebaseUser.uid, 'user_profile', 'user_profile');
      const snap = await getDoc(ref);
      const root: any = snap.exists() ? (snap.data() as any) : {};
      const existingArr: any[] = Array.isArray(root.profiles) ? root.profiles : [];

      // Compute mapped next value from UI
      const mapped = mapUiToFirestore(data);

      // Enforce immutability for unique identifiers after initial creation
      const existingFirst = existingArr[0] || {};
      const existingAccId = (existingFirst?.account_id ?? '') as string;
      const existingMemId = (existingFirst?.member_id ?? '') as string;
      const existingEmail = (existingFirst?.email ?? '') as string;

      const attempted: Array<{ field: string; from: any; to: any }> = [];

      if (existingAccId && String(mapped.account_id || '') !== String(existingAccId)) {
        attempted.push({ field: 'account_id', from: existingAccId, to: mapped.account_id });
        mapped.account_id = existingAccId;
      }
      if (existingMemId && String(mapped.member_id || '') !== String(existingMemId)) {
        attempted.push({ field: 'member_id', from: existingMemId, to: mapped.member_id });
        mapped.member_id = existingMemId;
      }
      if (existingEmail && String(mapped.email || '') !== String(existingEmail)) {
        attempted.push({ field: 'email', from: existingEmail, to: mapped.email });
        mapped.email = existingEmail;
      }
      // If email not yet set in profile, prefer the authenticated user's email
      if (!existingEmail && (!mapped.email || String(mapped.email).trim() === '') && (firebaseUser.email || user?.email)) {
        mapped.email = (firebaseUser.email || user?.email || '').trim();
      }

      if (attempted.length > 0) {
        try {
          console.warn('🔒 PROFILE: Immutable identifier change blocked', { uid: firebaseUser.uid, attempted });
          try { (await import('../lib/logging/secureLogger')).logImmutableBlocked(firebaseUser.uid, attempted, 'Profile.save'); } catch {}
          try { window.dispatchEvent(new CustomEvent('identity:immutable_block', { detail: { uid: firebaseUser.uid, attempted } })); } catch {}
        } catch {}
      }

      // Detect whether any profile fields actually changed (ignore timestamp-only updates)
      const profileKeys = [
        'account_id','member_id','first_name','last_name','phone','secondary_phone','email','secondary_email',
        'street_address','street_address_line_2','city','state_province','postal_code','country',
        'x','linkedin','youtube','telegram','facebook'
      ];

      const hasFieldChanges = profileKeys.some((k) => {
        const prevVal = (existingFirst?.[k] ?? '');
        const nextVal = (mapped?.[k] ?? '');
        return String(prevVal) !== String(nextVal);
      });

      if (!hasFieldChanges && existingArr.length > 0) {
        // No actual profile field changes; skip write to avoid bumping profile_lastupdated
        console.log('ℹ️ PROFILE: Skipping save (no profile field changes)');
        return;
      }

      // Apply update to first profile entry (index 0)
      const nextArr = existingArr.slice();
      if (nextArr.length === 0) nextArr.push(mapped);
      else nextArr[0] = { ...nextArr[0], ...mapped };

      // Overwrite with exact schema (no extra/redundant fields)
      await setDoc(ref, { profiles: nextArr, profile_lastupdated: serverTimestamp() }, { merge: false });
      console.log('✅ PROFILE: Successfully saved profile to Firebase (array schema)');

      // Phone verification (2026-08-14): a SAVED change to a non-empty
      // primaryPhone kicks off the SMS double-opt-in text. Fire-and-forget —
      // requestSmsVerification never throws — and never re-trigger when the
      // saved number is the one that is already confirmed.
      try {
        const prevPhone = String(existingFirst?.phone ?? '').trim();
        const nextPhone = String(mapped.phone ?? '').trim();
        const consent = smsConsentRef.current;
        const alreadyConfirmed =
          consent.status === 'confirmed' && maskPhone(nextPhone) === consent.phoneMasked;
        if (nextPhone && nextPhone !== prevPhone && !alreadyConfirmed) {
          void requestSmsVerification(nextPhone);
        }
      } catch {
        /* verification is best-effort — the profile save already succeeded */
      }
      
      // Show success indicator
      setSaveIndicator({
        visible: true,
        isError: false,
        message: 'profile saved to cloud'
      });
      
      setTimeout(() => {
        setSaveIndicator(prev => ({ ...prev, visible: false }));
      }, 2000);
      
    } catch (error: any) {
      console.error('❌ PROFILE: Error saving profile to Firebase:', error);
      
      // Show error indicator
      setSaveIndicator({
        visible: true,
        isError: true,
        message: 'error saving to cloud'
      });
      
      setTimeout(() => {
        setSaveIndicator(prev => ({ ...prev, visible: false }));
      }, 3000);
    }
  }).current;

  // Function that's called by the debounce to save the profile to localStorage (as cache)
  const saveProfileFunc = useRef((data: ProfileData) => {
    // Avoid caching while logged out
    if (!isAuthenticatedRef.current) {
      return;
    }
    // Use the centralized profile management function for localStorage caching
    const success = saveProfileData(data);
    console.log(success ? '✅ PROFILE: Cached to localStorage' : '❌ PROFILE: Failed to cache to localStorage');
  }).current;
  
  // Debounced functions
  const debouncedSaveToFirebase = useRef(
    debounce(saveProfileToFirebase, 2000) // 2 second debounce for Firebase (consistent with webapp standard)
  ).current;
  
  const debouncedSave = useRef(
    debounce(saveProfileFunc, 500) // 0.5 second debounce for localStorage cache
  ).current;
  
  // Enhanced resize handler for all device sizes
  useEffect(() => {
    // Handle responsive viewport detection
    const handleResize = () => {
      const width = window.innerWidth;
      // Enable mobile view for devices under 1024px (covering mobile and tablet)
      setIsMobileView(width <= 1024);
    };
    
    // Initial check
    handleResize();
    
    // Listen for resize events
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Tracking function for changes
  const trackChange = (change: Version['changes'][0]) => 
    setPendingChanges(prev => [...prev, change]);
  
  // Function to handle version changes
  const loadVersion = (versionId: string) => {
    setSelectedVersion(versionId);
    // Here you would load version-specific data if needed
    saveToLocalStorage(STORAGE_KEYS.SELECTED_VERSION, versionId);
  };
  
  // Function to generate unique IDs
  function generateUniqueId(type: 'account' | 'member'): string {
    return generateUniqueProfileId(type);
  }
  
  // Form validation function with enhanced validation rules
  const validateField = (name: string, value: string): string | undefined => {
    // Skip validation for empty fields (unless required)
    if (!value) return undefined;
    
    switch(name) {
      case 'email':
      case 'secondaryEmail':
        // More comprehensive email validation
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
          return 'Please enter a valid email address (example: name@example.com)';
        }
        break;
        
      case 'primaryPhone':
      case 'secondaryPhone':
        // More comprehensive phone validation allowing international formats
        if (!/^(\+\d{1,3}[ -]?)?\(?\d{3}\)?[ -]?\d{3}[ -]?\d{4}$/.test(value)) {
          return 'Please enter a valid phone number (example: (123) 456-7890 or +1-123-456-7890)';
        }
        break;
        
      case 'postalCode':
        // Support common postal code formats (US, UK, Canada)
        if (!/^[a-zA-Z0-9 -]{3,10}$/.test(value)) {
          return 'Please enter a valid postal code (example: 12345 or A1B 2C3)';
        }
        break;
        
      case 'xUrl':
        // X (Twitter) URL validation
        if (!/^https?:\/\/(www\.)?(twitter\.com|x\.com)\/[a-zA-Z0-9_]+\/?$/.test(value)) {
          return 'Please enter a valid X (Twitter) URL (example: https://x.com/yourname)';
        }
        break;
        
      case 'linkedinUrl':
        // More comprehensive LinkedIn URL validation
        if (!/(linkedin\.com\/)(in\/)?[a-zA-Z0-9_-]+\/?/.test(value)) {
          return 'Please enter a valid LinkedIn URL (example: https://linkedin.com/in/yourprofile)';
        }
        break;
        
      case 'youtubeUrl':
        // YouTube URL validation
        if (!/^https?:\/\/(www\.)?(youtube\.com\/(c\/|channel\/|user\/|@)|youtu\.be\/)[a-zA-Z0-9_-]+\/?$/.test(value)) {
          return 'Please enter a valid YouTube URL (example: https://youtube.com/@yourchannel)';
        }
        break;
        
      case 'telegramUrl':
        // Telegram URL validation
        if (!/^https?:\/\/(www\.)?(t\.me|telegram\.me)\/[a-zA-Z0-9_]+\/?$/.test(value)) {
          return 'Please enter a valid Telegram URL (example: https://t.me/yourusername)';
        }
        break;
        
      case 'facebookUrl':
        // Facebook URL validation
        if (!/^https?:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9_.]+\/?$/.test(value)) {
          return 'Please enter a valid Facebook URL (example: https://facebook.com/yourname)';
        }
        break;
        
      case 'firstName':
      case 'lastName':
        if (!/^[a-zA-Z\s\-'\.]{2,50}$/.test(value)) {
          return 'Please enter a valid name (2-50 characters, letters, spaces, hyphens, apostrophes)';
        }
        break;
        
      case 'country':
        if (!/^[a-zA-Z\s\-'\.]{2,50}$/.test(value)) {
          return 'Please enter a valid country name';
        }
        break;
        
      case 'city':
        if (!/^[a-zA-Z\s\-'\.]{2,50}$/.test(value)) {
          return 'Please enter a valid city name';
        }
        break;
        
      case 'state':
        if (!/^[a-zA-Z\s\-'\.]{2,50}$/.test(value)) {
          return 'Please enter a valid state or province';
        }
        break;
    }
    return undefined;
  };
  
  // Handle form input changes
  // Reference to store the current timeout for debounced saving
  const saveTimeoutRef = React.useRef<number | null>(null);
  
  // Handle input focus to clear validation errors while typing
  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    
    // When a field gets focus, clear any error message for a better user experience
    // This allows users to see the error only after they've finished typing
    setErrors(prev => ({
      ...prev,
      [name]: undefined
    }));
    
    // Update the save indicator
    setSaveIndicator({
      visible: true,
      isError: false,
      message: 'editing...'
    });
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Validate the field (but don't block typing)
    const error = validateField(name, value);
    
    // Always update the form data immediately regardless of validation
    // to ensure the user can continue typing
    const updatedProfile = {
      ...profileData,
      [name]: value
    };
    
    // Ensure the email is always the authenticated user's email
    if (isAuthenticated && user?.email && name !== 'email') {
      updatedProfile.email = user.email;
    }
    
    // Always update the state to reflect what the user is typing
    setProfileData(updatedProfile);
    
    // Only show validation errors for non-empty fields
    // This way, empty fields don't show errors while typing
    if (value.trim() !== '') {
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
      
      // Update valid fields status for visual feedback
      setValidFields(prev => ({
        ...prev,
        [name]: !error // Field is valid if there's no error
      }));
    } else {
      // Clear errors for empty fields
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
      
      // Empty fields are neither valid nor invalid
      setValidFields(prev => ({
        ...prev,
        [name]: false
      }));
    }
    
    // Track this change for version control
    trackChange({
      type: 'profile',
      action: 'update',
      target: name,
      details: `Changed ${name} to "${value}"`
    });
    
    // Clear any existing timeout to prevent multiple saves
    if (saveTimeoutRef.current) {
      window.clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    // Always update the UI state to show what the user has typed
    // However, debounce the actual saving to localStorage
    
    // Set a temporary saving state
    if (value.trim() !== '' && error) {
      // If the field has content but has validation errors, show a warning
      setSaveIndicator({
        visible: true,
        isError: true,
        message: 'validation needed - editing...'
      });
    } else {
      // Otherwise show a "typing" indicator
      setSaveIndicator({
        visible: true,
        isError: false,
        message: 'editing...'
      });
    }
    
    // Set a new timeout for debounced saving
    saveTimeoutRef.current = window.setTimeout(() => {
      // Only save to localStorage if:
      // 1. The field is empty (allowing clearing of fields)
      // 2. The field is valid (no validation errors)
      if (value.trim() === '' || !error) {
        // Use the existing debouncedSave function
        debouncedSave(updatedProfile);
      } else {
        // If validation fails, update the UI but don't save
        setSaveIndicator({
          visible: true,
          isError: true,
          message: 'validation error - not saved'
        });
        
        // Clear save indicator after a delay
        setTimeout(() => {
          setSaveIndicator({
            visible: false,
            isError: false,
            message: ''
          });
        }, 2000);
      }
    }, 2000); // 2000ms debounce delay for typing (consistent with webapp standard)
  };

  // Ensure realtime listener stops reacting when logged out
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    let unsubscribe: (() => void) | undefined;

    const setup = async () => {
      try {
        let firebaseUser = firebaseAuth.currentUser;
        if (!firebaseUser) {
          // Wait briefly for Firebase auth state to propagate instead of redirecting
          await new Promise(resolve => setTimeout(resolve, 500));
          firebaseUser = firebaseAuth.currentUser;
          if (!firebaseUser) return; // Firebase still not ready, skip
        }
        if (!firebaseUser?.uid) return;
        const userDocRef = doc(db, 'users', firebaseUser.uid, 'user_profile', 'user_profile');
        unsubscribe = onSnapshot(userDocRef, (snap) => {
          if (!isAuthenticatedRef.current) return;
          if (snap.exists()) {
            const data = snap.data() as any;
            const mapped = mapFirestoreToUi(data);
            if (mapped) {
              setProfileData(mapped);
              saveToLocalStorage(STORAGE_KEYS.PROFILE_DATA, mapped);
            }
          }
        });
      } catch (e) {
        console.error('❌ PROFILE: Error setting up realtime listener:', e);
      }
    };

    setup();
    return () => { try { if (unsubscribe) unsubscribe(); } catch {} };
  }, [isAuthenticated, user]);

  return (
    <>
      <SeoHead
        tabTitle="profile"
        title="Profile | prest0"
        description="review and manage your prest0 profile details, identity, and account preferences"
      />
      {/* Add meta viewport tag for proper mobile display */}
      <ViewportMeta />
      
      <ProfileContainer>
        {/* Add GlobalMobileStyle component */}
        <GlobalMobileStyle />
        
        <FadeIn>
          <FormSection>
            <SectionTitle>account profile</SectionTitle>
            
            {/* Google Account Indicator */}
            {user?.googleAuth && (
              <GoogleAccountIndicator>
                <GoogleIcon style={{ width: '20px', height: '20px', fontSize: '14px', marginRight: '10px' }}>G</GoogleIcon>
                <span>Your profile is synced with your Google account</span>
              </GoogleAccountIndicator>
            )}
            
            <ProfileSections>
              <IdSection>
                <FormGroup>
                  <IdLabel>
                    account ID
                    {!user?.googleAuth && (
                      <EmailBadge title="Email Account ID">
                        <span>@</span>
                      </EmailBadge>
                    )}
                  </IdLabel>
                  <ReadOnlyField>{profileData.accountId}</ReadOnlyField>
                </FormGroup>
              </IdSection>
              
              <IdSection>
                <FormGroup>
                  <IdLabel>
                    member ID
                  </IdLabel>
                  <ReadOnlyField>{profileData.memberId}</ReadOnlyField>
                </FormGroup>
              </IdSection>
              
              {/* Personal Information Section */}
              <Section>
                <SectionHeading>personal information</SectionHeading>
                
                <FormRow>
                  <FormGroup>
                    <Label>
                      first name
                      {user?.googleAuth && (
                        <GoogleBadge title="Synced with your Google account">
                          <GoogleIcon>G</GoogleIcon>
                        </GoogleBadge>
                      )}
                    </Label>
                    <NameInput
                      type="text"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      placeholder="enter your first name"
                      aria-label="First name"
                      isValid={validFields.firstName}
                    />
                    {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>
                      last name
                      {user?.googleAuth && (
                        <GoogleBadge title="Synced with your Google account">
                          <GoogleIcon>G</GoogleIcon>
                        </GoogleBadge>
                      )}
                    </Label>
                    <NameInput
                      type="text"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      placeholder="enter your last name"
                      aria-label="Last name"
                      isValid={validFields.lastName}
                    />
                    {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
                  </FormGroup>
                </FormRow>
              </Section>

              {/* Organization Section (re-keyed 2026-08-16): the ACTIVE
                  workspace's organization — read-only projection here, the
                  editor is its own page (/organization). */}
              <Section>
                <SectionHeading>organization</SectionHeading>
                <FormRow>
                  <FormGroup>
                    <Label>organization</Label>
                    <NameInput
                      type="text"
                      name="orgName"
                      value={
                        orgCardLoaded
                          ? (orgCard?.name || 'not set yet — add it on the organization page')
                          : 'loading…'
                      }
                      readOnly
                      disabled
                      aria-label="Active workspace organization name"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>organization size</Label>
                    <NameInput
                      type="text"
                      name="orgSize"
                      value={
                        orgCardLoaded
                          ? (ORG_SIZE_BANDS.find((b) => b.value === orgCard?.sizeBand)?.label || 'not set yet')
                          : 'loading…'
                      }
                      readOnly
                      disabled
                      aria-label="Active workspace organization size"
                    />
                    <div style={{ gridColumn: '1 / -1', fontSize: '0.82rem', marginTop: '0.25rem' }}>
                      {orgCard?.role && (
                        <span style={{ color: '#98917F', marginRight: '0.6rem' }}>your role: {orgCard.role}</span>
                      )}
                      <a href="/organization" style={{ color: '#7A5C00', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                        view or edit on the organization page →
                      </a>
                    </div>
                  </FormGroup>
                </FormRow>
              </Section>

              {/* Contact Information Section - collapsible on mobile.
                  ("Your Prest0n Number" card removed 2026-08-16 — see note at
                  the former tenantPhone state.) */}
              <Section>
                <SectionHeading>contact information</SectionHeading>

                <CollapsibleSection isCollapsed={collapsedSections.contactInfo}>
                  <FormRow>
                    <FormGroup>
                      <Label>phone</Label>
                      <PhoneInput
                        type="tel"
                        name="primaryPhone"
                        value={profileData.primaryPhone}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        placeholder="enter your phone number (e.g., 123-456-7890)"
                        aria-label="Primary phone number"
                        isValid={validFields.primaryPhone}
                      />
                      {errors.primaryPhone && <ErrorMessage>{errors.primaryPhone}</ErrorMessage>}
                      {/* Live SMS verification status (2026-08-14) — quiet
                          when there is no consent record at all. */}
                      {smsConsent.status === 'confirmed' && (
                        <PhoneVerifyChip $tone="green">verified ✓</PhoneVerifyChip>
                      )}
                      {smsConsent.status === 'pending' && (
                        <PhoneVerifyChip $tone="gold">verification pending — check your texts</PhoneVerifyChip>
                      )}
                      {smsConsent.status === 'expired' && (
                        <PhoneVerifyChip $tone="gray">verification expired</PhoneVerifyChip>
                      )}
                      {smsConsent.status === 'opted_out' && (
                        <PhoneVerifyChip $tone="gray">opted out — reply START to re-enable</PhoneVerifyChip>
                      )}
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>secondary phone</Label>
                      <PhoneInput
                        type="tel"
                        name="secondaryPhone"
                        value={profileData.secondaryPhone}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        placeholder="enter your secondary phone (e.g., 123-456-7890)"
                        aria-label="Secondary phone number"
                        isValid={validFields.secondaryPhone}
                      />
                      {errors.secondaryPhone && <ErrorMessage>{errors.secondaryPhone}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>
                  
                  <FormRow>
                    <FormGroup>
                      <Label>
                        email
                        {user?.googleAuth && (
                          <GoogleBadge title="Synced with your Google account">
                            <GoogleIcon>G</GoogleIcon>
                          </GoogleBadge>
                        )}
                      </Label>
                      <ReadOnlyField style={{
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                        letterSpacing: "0.01em",
                        background: "var(--brand-canvas)",
                        color: "var(--brand-text-secondary)",
                        textAlign: "left",
                        fontSize: "0.95rem" 
                      }}>
                        {(user?.googleAuth ? user.email : profileData.email) || "No email found"}
                      </ReadOnlyField>
                      {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>
                  
                  <FormRow>
                    <FormGroup>
                      <Label>secondary email</Label>
                      <EmailInput
                        type="email"
                        name="secondaryEmail"
                        value={profileData.secondaryEmail}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        placeholder="enter your secondary email (e.g., name@example.com)"
                        aria-label="Secondary email address"
                        isValid={validFields.secondaryEmail}
                      />
                      {errors.secondaryEmail && <ErrorMessage>{errors.secondaryEmail}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>
                </CollapsibleSection>
                
                {/* Toggle button for mobile view */}
                <ToggleButton onClick={() => toggleSection('contactInfo')}>
                  {collapsedSections.contactInfo ? 'Show more' : 'Show less'}
                  <svg 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    style={{ transform: collapsedSections.contactInfo ? 'rotate(0deg)' : 'rotate(180deg)' }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </ToggleButton>
              </Section>
              
              {/* Address Section - collapsible on mobile */}
              <Section>
                <SectionHeading>address information</SectionHeading>
                
                <CollapsibleSection isCollapsed={collapsedSections.addressInfo}>
                  <FormRow>
                    <FormGroup>
                      <Label>street address</Label>
                      <StyledInput
                        type="text"
                        name="streetAddress"
                        value={profileData.streetAddress}
                        onChange={handleChange}
                        placeholder="enter your street address (e.g., 123 Main Street)"
                        aria-label="Street address"
                        isValid={validFields.streetAddress}
                      />
                      {errors.streetAddress && <ErrorMessage>{errors.streetAddress}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>
                  
                  <FormRow>
                    <FormGroup>
                      <Label>street address line 2</Label>
                      <StyledInput
                        type="text"
                        name="streetAddress2"
                        value={profileData.streetAddress2}
                        onChange={handleChange}
                        placeholder="enter your apt, suite, unit, etc. (e.g., Apt 4B)"
                        aria-label="Street address line 2"
                        isValid={validFields.streetAddress2}
                      />
                      {errors.streetAddress2 && <ErrorMessage>{errors.streetAddress2}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>
                  
                  <FormRow>
                    <FormGroup>
                      <Label>city</Label>
                      <StyledInput
                        type="text"
                        name="city"
                        value={profileData.city}
                        onChange={handleChange}
                        placeholder="enter your city (e.g., San Francisco)"
                        aria-label="City"
                        isValid={validFields.city}
                      />
                      {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>
                  
                  <FormRow>
                    <FormGroup>
                      <Label>state/province</Label>
                      <ShortInput
                        type="text"
                        name="state"
                        value={profileData.state}
                        onChange={handleChange}
                        placeholder="enter your state/province (e.g., CA)"
                        aria-label="State or province"
                        isValid={validFields.state}
                      />
                      {errors.state && <ErrorMessage>{errors.state}</ErrorMessage>}
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>postal code</Label>
                      <ShortInput
                        type="text"
                        name="postalCode"
                        value={profileData.postalCode}
                        onChange={handleChange}
                        placeholder="enter your postal code (e.g., 12345)"
                        aria-label="Postal code"
                        isValid={validFields.postalCode}
                      />
                      {errors.postalCode && <ErrorMessage>{errors.postalCode}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>
                  
                  <FormRow>
                    <FormGroup>
                      <Label>country</Label>
                      <StyledInput
                        type="text"
                        name="country"
                        value={profileData.country}
                        onChange={handleChange}
                        placeholder="enter your country (e.g., United States)"
                        aria-label="Country"
                        isValid={validFields.country}
                      />
                      {errors.country && <ErrorMessage>{errors.country}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>
                </CollapsibleSection>
                
                {/* Toggle button for mobile view */}
                <ToggleButton onClick={() => toggleSection('addressInfo')}>
                  {collapsedSections.addressInfo ? 'Show more' : 'Show less'}
                  <svg 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    style={{ transform: collapsedSections.addressInfo ? 'rotate(0deg)' : 'rotate(180deg)' }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </ToggleButton>
              </Section>
              
              <Section>
                <SectionHeading>social profiles</SectionHeading>
                
                <FormRow>
                  <FormGroup>
                    <Label>x (twitter)</Label>
                    <UrlInput
                      type="text"
                      name="xUrl"
                      value={profileData.xUrl || ''}
                      onChange={handleChange}
                      placeholder="enter your X (Twitter) URL (e.g., https://x.com/yourname)"
                      aria-label="X (Twitter) profile URL"
                      isValid={validFields.xUrl}
                    />
                    {errors.xUrl && <ErrorMessage>{errors.xUrl}</ErrorMessage>}
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label>linkedin</Label>
                    <UrlInput
                      type="text"
                      name="linkedinUrl"
                      value={profileData.linkedinUrl || ''}
                      onChange={handleChange}
                      placeholder="enter your LinkedIn URL (e.g., https://linkedin.com/in/yourname)"
                      aria-label="LinkedIn profile URL"
                      isValid={validFields.linkedinUrl}
                    />
                    {errors.linkedinUrl && <ErrorMessage>{errors.linkedinUrl}</ErrorMessage>}
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label>youtube</Label>
                    <UrlInput
                      type="text"
                      name="youtubeUrl"
                      value={profileData.youtubeUrl || ''}
                      onChange={handleChange}
                      placeholder="enter your YouTube URL (e.g., https://youtube.com/@yourchannel)"
                      aria-label="YouTube channel URL"
                      isValid={validFields.youtubeUrl}
                    />
                    {errors.youtubeUrl && <ErrorMessage>{errors.youtubeUrl}</ErrorMessage>}
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label>telegram</Label>
                    <UrlInput
                      type="text"
                      name="telegramUrl"
                      value={profileData.telegramUrl || ''}
                      onChange={handleChange}
                      placeholder="enter your Telegram URL (e.g., https://t.me/yourusername)"
                      aria-label="Telegram profile URL"
                      isValid={validFields.telegramUrl}
                    />
                    {errors.telegramUrl && <ErrorMessage>{errors.telegramUrl}</ErrorMessage>}
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label>facebook</Label>
                    <UrlInput
                      type="text"
                      name="facebookUrl"
                      value={profileData.facebookUrl || ''}
                      onChange={handleChange}
                      placeholder="enter your Facebook URL (e.g., https://facebook.com/yourname)"
                      aria-label="Facebook profile URL"
                      isValid={validFields.facebookUrl}
                    />
                    {errors.facebookUrl && <ErrorMessage>{errors.facebookUrl}</ErrorMessage>}
                  </FormGroup>
                </FormRow>
              </Section>
            </ProfileSections>
          </FormSection>
          
          {/* Export Section with coming soon badge */}
          <ExportSection>
            <ExportTitle>
              export profile
              <ComingSoonBadge>coming soon</ComingSoonBadge>
            </ExportTitle>
            
            <CollapsibleSection isCollapsed={collapsedSections.exportInfo}>
              <ExportDescription>
                export your profile data in various formats for use in other systems,
                including the ability to create a portable digital identity.
              </ExportDescription>
              
              <ExportFeatureList>
                <ExportFeature>
                  <FeatureTitle>JSON format</FeatureTitle>
                  <FeatureDescription>
                    export your data in structured JSON format for use in 
                    developer tools and programming environments.
                  </FeatureDescription>
                </ExportFeature>
                
                <ExportFeature>
                  <FeatureTitle>CSV export</FeatureTitle>
                  <FeatureDescription>
                    download your profile information in CSV format
                    for easy import into spreadsheet applications.
                  </FeatureDescription>
                </ExportFeature>
                
                <ExportFeature>
                  <FeatureTitle>portable ID</FeatureTitle>
                  <FeatureDescription>
                    create a portable digital identity that can be
                    shared across the prest0 ecosystem.
                  </FeatureDescription>
                </ExportFeature>
              </ExportFeatureList>
            </CollapsibleSection>
            
            {/* Toggle button for mobile view */}
            <ToggleButton onClick={() => toggleSection('exportInfo')}>
              {collapsedSections.exportInfo ? 'Show more' : 'Show less'}
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ transform: collapsedSections.exportInfo ? 'rotate(0deg)' : 'rotate(180deg)' }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </ToggleButton>
          </ExportSection>
        </FadeIn>
        
        {/* Save indicator */}
        <SaveIndicator 
          visible={saveIndicator.visible} 
          isError={saveIndicator.isError}
        >
          {saveIndicator.isError ? null : <Spinner />}
          {saveIndicator.message}
        </SaveIndicator>
        
        {/* VersionControl and PromptBar removed — GlobalChatSystem will be added later */}
      </ProfileContainer>
      
      <FooterWrapper>
        <GlobalFooter />
      </FooterWrapper>
    </>
  );
};

export default Profile;